/**
 * Avatar Cache Manager
 * Handles avatar image fetching and caching with lazy loading support
 */

import axiosInstance from 'Services/APIService';

interface AvatarCache {
  url: string;
  timestamp: number;
}

class AvatarCacheManager {
  private cache: Map<string, AvatarCache> = new Map();
  private loadingPromises: Map<string, Promise<string | null>> = new Map();
  private readonly AVATAR_TTL = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Fetch avatar with caching and deduplication
   */
  async fetchAvatar(avatarPath: string): Promise<string | null> {
    if (!avatarPath) return null;

    // Return cached URL if valid
    const cached = this.cache.get(avatarPath);
    if (cached && Date.now() - cached.timestamp < this.AVATAR_TTL) {
      return cached.url;
    }

    // Deduplicate identical requests
    if (this.loadingPromises.has(avatarPath)) {
      return this.loadingPromises.get(avatarPath)!;
    }

    // Fetch new avatar
    const promise = this._fetchAvatarInternal(avatarPath);
    this.loadingPromises.set(avatarPath, promise);

    try {
      return await promise;
    } finally {
      this.loadingPromises.delete(avatarPath);
    }
  }

  /**
   * Internal avatar fetch logic
   */
  private async _fetchAvatarInternal(avatarPath: string): Promise<string | null> {
    try {
      const response = await axiosInstance.get(
        `/avatar/get-avatar?path=${encodeURIComponent(avatarPath)}`,
        { responseType: 'blob', timeout: 8000 } // 8 second timeout
      );
      const imageUrl = URL.createObjectURL(response.data);
      this.cache.set(avatarPath, {
        url: imageUrl,
        timestamp: Date.now()
      });
      return imageUrl;
    } catch (error) {
      console.error(`Error fetching avatar: ${avatarPath}`, error);
      return null;
    }
  }

  /**
   * Batch fetch avatars with concurrency control
   */
  async fetchAvatarsBatch(
    avatarPaths: (string | undefined)[],
    maxConcurrent: number = 3
  ): Promise<Map<string, string | null>> {
    const results = new Map<string, string | null>();
    const validPaths = avatarPaths.filter((p): p is string => !!p);

    if (validPaths.length === 0) {
      return results;
    }

    // Process in batches to avoid overwhelming the server
    for (let i = 0; i < validPaths.length; i += maxConcurrent) {
      const batch = validPaths.slice(i, i + maxConcurrent);
      const batchResults = await Promise.allSettled<{ path: string; url: string | null }>(
        batch.map(async (path) => {
          const url = await this.fetchAvatar(path);
          return { path, url };
        })
      );

      batchResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          results.set(result.value.path, result.value.url);
        } else {
          results.set(batch[index], null);
        }
      });
    }

    return results;
  }

  /**
   * Revoke blob URLs to free memory
   */
  revokeBlobUrls(urls: (string | null | undefined)[]): void {
    urls.forEach((url) => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
  }

  /**
   * Clear avatar cache
   */
  clearCache(avatarPath?: string): void {
    if (avatarPath) {
      const entry = this.cache.get(avatarPath);
      if (entry) {
        URL.revokeObjectURL(entry.url);
      }
      this.cache.delete(avatarPath);
    } else {
      // Clear all
      this.cache.forEach((entry) => {
        URL.revokeObjectURL(entry.url);
      });
      this.cache.clear();
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    cachedAvatars: number;
    pendingLoads: number;
  } {
    return {
      cachedAvatars: this.cache.size,
      pendingLoads: this.loadingPromises.size
    };
  }
}

export const avatarCacheManager = new AvatarCacheManager();
